import { Component } from '@grafikr/components';
import fastdom from 'fastdom';

import repaint from 'utils/repaint';

export default Component((node) => {
  const { stop, start } = node.dataset;

  if (!stop) {
    throw new Error('Missing data-stop attribute.');
  }

  const stopElement = document.querySelector<HTMLElement>(stop);
  const startElement = start ? document.querySelector<HTMLElement>(start) : null;

  if (!stopElement) {
    throw new Error(`${stop} attribute, didn't match any elements on site.`);
  }

  node.hidden = true;

  let hasStarted = !startElement; // If no start element, consider it started
  let beforeStop = true;

  const updateVisibility = () => {
    const shouldBeActive = hasStarted && beforeStop;

    if (shouldBeActive) {
      fastdom.measure(() => {
        if (node.hidden) {
          node.hidden = false;
          repaint(node);
        }
      });

      fastdom.mutate(() => {
        node.classList.add('is-active');
      });
    } else {
      fastdom.mutate(() => {
        node.classList.remove('is-active');
      });
    }
  };

  const handleStartIntersection = (entry: IntersectionObserverEntry) => {
    const rootTop = entry.rootBounds?.top ?? 0;
    hasStarted = entry.boundingClientRect.top <= rootTop;
    updateVisibility();
  };

  const handleStopIntersection = (entry: IntersectionObserverEntry) => {
    const rootTop = entry.rootBounds?.top ?? 0;
    beforeStop = entry.boundingClientRect.top > rootTop;
    updateVisibility();
  };

  const stopObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        handleStopIntersection(entry);
      });
    },
    {
      rootMargin: node.dataset.offset ?? '0px',
    },
  );

  const startObserver = startElement
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            handleStartIntersection(entry);
          });
        },
        {
          rootMargin: node.dataset.startOffset ?? '0px',
        },
      )
    : null;

  if (startElement && startObserver) {
    startObserver.observe(startElement);
  }

  stopObserver.observe(stopElement);

  updateVisibility();
});
